/**
 * HTTP client wrapper for the FTL API.
 *
 * Uses the native `fetch` API -- no axios dependency required.
 * Handles JWT auth, automatic 401 handling, JSON parsing, and typed errors.
 */

import type { ApiError } from "./types";
import { isScopeBlockPayload, useScopeBlockStore } from "@/stores/scopeBlock";
import { isCreditBlockPayload, useCreditBlockStore } from "@/stores/creditBlock";
import { isEditConflictPayload, useEditConflictStore } from "@/stores/editConflict";
import { useAppErrorNoticeStore } from "@/stores/appErrorNotice";
import { getActAsRole } from "@/lib/actAsRole";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const BASE_URL: string =
  import.meta.env.VITE_API_URL ?? "/api/v1";

const TOKEN_STORAGE_KEY = "ftl_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "ftl_refresh_token";
// Guest/meeting JWTs live in their OWN tab-scoped slot — NEVER the shared
// `ftl_access_token`. A guest token is a short-lived, wrong-audience
// credential (is_guest=true); writing it into the real auth slot is what
// poisoned logged-in FTL sessions and produced the recurring "missing access
// token" lockout: a guest-auth (or guest-refresh on any 401) overwrote the
// real JWT, after which the whole app believed the user was a guest and every
// host/meeting action failed client-side. Namespacing is the canonical fix —
// anonymous/guest sessions must not share storage with a real auth token
// (SuperTokens / Auth0 / Curity guidance). sessionStorage because a guest
// session is tab-scoped and must not outlive the tab.
const GUEST_TOKEN_STORAGE_KEY = "ftl_guest_token";

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
}

/**
 * Drop the session AND everything it fetched.
 *
 * THE DEFECT THIS CLOSES (row 1f6beae0, founder answer 581386ab "YES - fix it").
 * Every API response lives in ONE module-level QueryClient, and signing out used
 * to remove only the tokens. A second person signing in on the same browser tab
 * inside the cache's gcTime was served the FIRST person's cached responses on
 * every screen -- including another organization's data -- with no API call at
 * all. RLS, org scoping and the platform-admin gates cannot catch that, because
 * the data never leaves the browser. Query keys are not user- or org-scoped
 * (useOrgSummary, useOrgAICosts and the rest), so a key collides across users by
 * construction.
 *
 * WHY HERE AND NOT AT THE SIGN-OUT BUTTONS. There are FOUR paths that sign a
 * user out -- Topbar, ActivityRibbon, FelixChat and UserProfile -- and the row
 * named only three; I found the fourth while re-verifying it. Clearing at three
 * of four leaves the hole open, and the next sign-out path someone writes would
 * be open too. All four already call this function, so this is the one place
 * that cannot be forgotten.
 *
 * WHY A SUBSCRIBER AND NOT A DIRECT IMPORT. The first version imported the
 * QueryClient here. That made this module -- which almost everything imports --
 * construct a real QueryClient at IMPORT time, and six test files mock
 * @tanstack/react-query; one of them broke immediately with "QueryClient is not
 * a constructor". Inverting it keeps api/client.ts free of any react-query
 * dependency: the cache registers ITSELF, next to the thing it clears.
 *
 * The registry is the "configured but never connected" shape, so it is guarded:
 * api/queryClient.ts registers on import, App.tsx imports it to build the
 * provider, and a test asserts both the registration and the clearing.
 */
const logoutHandlers = new Set<() => void>();

/** Run ``fn`` whenever the session is cleared. Returns an unsubscribe. */
export function onSessionCleared(fn: () => void): () => void {
  logoutHandlers.add(fn);
  return () => logoutHandlers.delete(fn);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  for (const fn of logoutHandlers) fn();
}

/**
 * Guest/meeting JWT accessors. Stored in sessionStorage under a DISTINCT key
 * so a guest credential can never overwrite the real FTL session token.
 */
export function getGuestToken(): string | null {
  return sessionStorage.getItem(GUEST_TOKEN_STORAGE_KEY);
}

export function setGuestToken(token: string): void {
  sessionStorage.setItem(GUEST_TOKEN_STORAGE_KEY, token);
}

export function clearGuestToken(): void {
  sessionStorage.removeItem(GUEST_TOKEN_STORAGE_KEY);
}

/** The end page of the meeting a guest is on (/meeting/<id>, its lobby or its
 *  end page), or null off a meeting page. */
export function guestMeetingEndPath(pathname: string): string | null {
  const m = /^\/meeting\/([^/]+)(?:\/(?:lobby|end))?\/?$/.exec(pathname);
  return m ? `/meeting/${m[1]}/end` : null;
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

export class ApiRequestError extends Error {
  status: number;
  detail: string;
  errors?: ApiError["errors"];
  /**
   * The raw structured error body (the server's `detail`), preserved so a
   * consumer can render a structured payload as an actionable affordance
   * instead of dumping `detail`'s JSON string at the user. Example: the
   * baseline pre-flight 409 carries `{message, readiness}` (Patent G) — the
   * Baselines surface reads `error.data.readiness` to draw a checklist.
   * Undefined when the body was a plain string or unparseable.
   */
  data?: unknown;

  constructor(
    status: number,
    detail: string,
    errors?: ApiError["errors"],
    data?: unknown,
  ) {
    super(detail);
    this.name = "ApiRequestError";
    this.status = status;
    this.detail = detail;
    this.errors = errors;
    this.data = data;
  }
}

// ---------------------------------------------------------------------------
// Token refresh logic
// ---------------------------------------------------------------------------

/** Mutex to prevent concurrent refresh requests. */
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) {
      clearTokens();
      return false;
    }

    const data = await res.json();
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

/**
 * Try to refresh the access token exactly once. Concurrent callers share
 * the same in-flight promise so we never issue duplicate refresh requests.
 *
 * Exported so streaming callers (chatStream, chatAutonomous) — which bypass
 * executeRequest — can participate in the same mutex-protected refresh cycle.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = attemptTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Refresh BEFORE the access token expires, not after the server refuses it
 * (row bc96f3f0). The 10-11 dry run's three mid-meeting 401s (GET transcript
 * 13:02:47Z, POST media-stats 13:58:53Z and 14:03:04Z) were each the first
 * request after a member's 60-minute token lapsed: the client sent the dead
 * token, took the 401, refreshed and retried within the same second. Nothing
 * failed for the person, but every hour of every meeting put a 401 in the
 * logs for each participant, indistinguishable from a real auth fault.
 * Reading `exp` and refreshing inside a short skew window is the standard
 * client pattern (RFC 7519 §4.1.4 exp; the same skew oidc-client-ts and
 * auth0-spa-js apply before expiry).
 */
const REFRESH_SKEW_SECONDS = 60;

/** Seconds-since-epoch `exp` of a JWT, or null when it is not a readable JWT. */
export function jwtExpiry(token: string): number | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

export function accessTokenExpiresSoon(token: string, nowMs: number = Date.now()): boolean {
  const exp = jwtExpiry(token);
  return exp !== null && exp * 1000 - nowMs <= REFRESH_SKEW_SECONDS * 1000;
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

interface RequestOptions {
  method: string;
  path: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  /** If true, skip attaching the Authorization header entirely. */
  skipAuth?: boolean;
  /** Optional AbortSignal for request cancellation / timeout. */
  signal?: AbortSignal;
  /** A call the person did not make (usage telemetry): a failure is the caller's to log and never
   *  reaches Felix or his error notice (row 81273755: Home opened his panel with a raw 422). */
  background?: boolean;
  /** Internal flag to prevent infinite 401 → refresh → retry loops. */
  _retried?: boolean;
}

/**
 * Build a query-string from a params record, filtering out undefined/null.
 */
function buildQueryString(
  params?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] =>
      entry[1] !== undefined && entry[1] !== null,
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
  return `?${qs}`;
}

/**
 * Parse an error response body into a structured ApiError.
 */
async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    const data = await res.json();
    return {
      detail:
        typeof data.detail === "string"
          ? data.detail
          : JSON.stringify(data.detail ?? data),
      status: res.status,
      errors: Array.isArray(data.detail) ? data.detail : undefined,
    };
  } catch {
    return {
      detail: res.statusText || `Request failed with status ${res.status}`,
      status: res.status,
    };
  }
}

/**
 * FastAPI validation errors (422) arrive as an array of
 * `{type, loc, msg, input}` objects. JSON.stringify-ing that array surfaces raw
 * JSON to the user. This joins the human-readable `msg` fields (stripping the
 * "Value error, " prefix Pydantic prepends) into a friendly sentence. Returns
 * null when the value isn't a recognizable validation-error array so callers
 * fall back to their existing behavior.
 */
/**
 * Pull the human sentence out of a structured error body.
 *
 * Several endpoints raise with a DICT detail so the client can branch on a
 * machine-readable `error` code while still having something to show — e.g. the
 * project cap returns
 *   {error: "project_limit_reached",
 *    message: "Your plan includes 25 projects and you're using 30. Upgrade
 *              your plan to create more.",
 *    current, limit}
 *
 * `formatValidationDetail` only understands FastAPI's validation ARRAY shape,
 * so every one of these fell through to `JSON.stringify` and the user was shown
 * a raw blob of braces and quotes with the actual sentence buried inside it.
 * The backend had written a perfectly good message; the client threw it away.
 *
 * Deliberately narrow: only a non-empty string `message` (or `detail`) counts.
 * Anything else returns null and the caller keeps its existing fallback, so a
 * body we do not understand is never silently reduced to a misleading fragment.
 */
function messageFromDetail(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const rec = value as Record<string, unknown>;
  for (const key of ["message", "detail"]) {
    const v = rec[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

function formatValidationDetail(detailValue: unknown): string | null {
  if (!Array.isArray(detailValue)) return null;
  const msgs = detailValue
    .map((item) =>
      item && typeof item === "object" && typeof (item as { msg?: unknown }).msg === "string"
        ? (item as { msg: string }).msg.replace(/^Value error,\s*/, "")
        : null,
    )
    .filter((m): m is string => Boolean(m));
  if (!msgs.length) return null;
  // De-duplicate identical messages, join with a space so multi-field errors read as prose.
  return Array.from(new Set(msgs)).join(" ");
}

/**
 * Execute a single HTTP request against the API.
 */
async function executeRequest<T>(opts: RequestOptions): Promise<T> {
  const url = `${BASE_URL}${opts.path}${buildQueryString(opts.params)}`;

  const headers: Record<string, string> = {
    ...opts.headers,
  };

  // A member session about to lapse is renewed first, so the request goes out
  // with a live token instead of collecting a 401 (see accessTokenExpiresSoon).
  // Only the real session: a guest JWT is renewed by guest-auth on its 401.
  if (!opts.skipAuth && !opts._retried) {
    const current = getAccessToken();
    if (current && getRefreshToken() && accessTokenExpiresSoon(current)) {
      await refreshAccessToken();
    }
  }

  // Attach auth header when available. A real FTL session token ALWAYS wins;
  // the guest token is only used when there is no real session (a true guest
  // viewing a meeting). This ordering is what guarantees a logged-in user is
  // never downgraded to guest scope even if a guest token lingers in the tab.
  if (!opts.skipAuth) {
    const token = getAccessToken() ?? getGuestToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // UAT act-as-role. Sent on EVERY authenticated request so a tester's whole
  // session behaves as the assumed role, not just the screen they picked it
  // on — a half-applied role would teach them the wrong thing about what that
  // role can actually see.
  //
  // This cannot grant anything. The backend narrows the effective role set
  // only when the requested role ranks at or below what the user genuinely
  // holds, and ignores it otherwise, so the header is a request rather than a
  // claim. Skipped alongside auth: an unauthenticated call has no roles to
  // narrow.
  if (!opts.skipAuth) {
    const actAs = getActAsRole();
    if (actAs) {
      headers["X-FTL-Act-As-Role"] = actAs;
    }
  }

  // Set Content-Type for requests with a body
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method: opts.method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // The app answered: a notice about this same request failing is out of date
  // (row 321d0d14).
  if (res.ok) {
    try {
      useAppErrorNoticeStore.getState().recovered(opts.method, opts.path);
    } catch {
      // The notice must never change what the caller receives.
    }
  }

  // Handle 204 No Content (e.g. DELETE)
  if (res.status === 204) {
    return undefined as T;
  }

  // Handle 401 -- attempt token refresh then retry once
  if (res.status === 401 && !opts.skipAuth && !opts._retried) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry with the new token — use _retried flag to prevent infinite loops
      return executeRequest<T>({ ...opts, _retried: true } as RequestOptions);
    }
    // This is a guest meeting session whose guest JWT expired — re-authenticate
    // and refresh ONLY the guest slot. We never touch `ftl_access_token` here:
    // doing so was the lockout bug. Only attempt this when there is no real FTL
    // session present (a logged-in user whose refresh genuinely failed should
    // go to login, not be silently swapped to guest scope).
    const guestLinkToken = sessionStorage.getItem("ftl_guest_link_token");
    const guestName = sessionStorage.getItem("ftl_guest_display_name");
    if (guestLinkToken && guestName && !getAccessToken()) {
      try {
        const guestRes = await fetch(`${BASE_URL}/meetings/guest-auth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The arrival id matters here as much as on the first join: without
          // it a 401 mid-meeting reads as a brand-new knock and the guest is
          // dropped back into the lobby. Read directly rather than importing,
          // to keep this low-level retry free of module cycles.
          body: JSON.stringify({
            token: guestLinkToken,
            display_name: guestName,
            arrival_id: sessionStorage.getItem("ftl_guest_arrival_id"),
            // Row cd7ca6c9: the transcription notice version accepted on the
            // join screen (GUEST_ACCEPTED_NOTICE_KEY, read directly for the
            // same no-cycles reason). Without it guest-auth refuses.
            accepted_notice_version: sessionStorage.getItem("ftl_guest_accepted_notice_version"),
            // Row 718e501d PR 2: the recording agreement, if the guest gave one
            // (GUEST_ACCEPTED_RECORDING_KEY, read directly for the same reason).
            accepted_recording_notice_version: sessionStorage.getItem(
              "ftl_guest_accepted_recording_version",
            ),
          }),
        });
        if (guestRes.ok) {
          const data = await guestRes.json();
          setGuestToken(data.guest_jwt);
          return executeRequest<T>({ ...opts, _retried: true } as RequestOptions);
        }
      } catch {
        // Guest re-auth failed: handled just below.
      }
      // A GUEST HAS NO ACCOUNT TO SIGN IN TO (row daefa45f). Once the host
      // ends the meeting, guest-auth refuses the link, and falling through to
      // the login redirect sent both guests of walk w10081240 to
      // /login?next=/meeting/<id>/end. On a meeting page the visit is over, so
      // the guest goes to that meeting's end page (or stays on it).
      const endPage = guestMeetingEndPath(window.location.pathname);
      if (endPage) {
        if (window.location.pathname !== endPage) window.location.href = endPage;
        throw new ApiRequestError(401, "This meeting has ended.");
      }
    }
    // THE END PAGE IS PUBLIC AND FINAL (row daefa45f, walk w10082230). It
    // clears the guest's session as it renders, so a request still in flight
    // from the room comes back 401 with no guest session left to recognise,
    // and both guests were sent to /login?next=/meeting/<id>/end. Nobody on a
    // meeting's end page is sent to sign in; the request just fails.
    if (/^\/meeting\/[^/]+\/end\/?$/.test(window.location.pathname)) {
      throw new ApiRequestError(401, "This meeting has ended.");
    }
    // Refresh failed -- redirect to login, preserving where they were so a deep link with
    // a non-reconstructable query string (e.g. GitHub's post-install
    // /settings/integrations?installation_id=…) survives the round-trip. Login reads `next`.
    clearTokens();
    const here = window.location.pathname + window.location.search;
    window.location.href =
      window.location.pathname === "/login"
        ? "/login"
        : `/login?next=${encodeURIComponent(here)}`;
    throw new ApiRequestError(401, "Session expired. Redirecting to login.");
  }

  // Non-success responses
  if (!res.ok) {
    // Read the body once. A 423 scope-block carries a structured detail
    // object (see baseline_service._scope_block_payload) that drives the
    // "create a change request" affordance rather than a raw error toast.
    let rawBody: unknown = null;
    try {
      rawBody = await res.json();
    } catch {
      /* non-JSON or empty body */
    }
    const detailValue =
      rawBody && typeof rawBody === "object"
        ? (rawBody as { detail?: unknown }).detail
        : undefined;

    if (res.status === 423 && isScopeBlockPayload(detailValue)) {
      // Surface the scope-block modal. Do NOT also report it to Felix as a
      // raw error — the modal is the intended, actionable affordance.
      useScopeBlockStore.getState().show(detailValue);
      throw new ApiRequestError(423, detailValue.message);
    }

    // Running out of AI credits is an upsell moment, not a crash. The payload is
    // checked at the TOP LEVEL (not `detail`) because CreditQuotaExceeded
    // serialises unwrapped — which is exactly why it used to fall through to the
    // generic branch below and get shown to the user as `JSON.stringify(rawBody)`.
    // Like the 423 above, it is NOT reported to Felix: the modal is the intended
    // affordance, and a quota block is expected behaviour rather than a defect.
    // Note this only catches the credit payload; other 402s (e.g. the billing
    // gate in dependencies.check_billing_active) carry a string `detail` and
    // still render correctly through the generic path.
    if (res.status === 402 && isCreditBlockPayload(rawBody)) {
      useCreditBlockStore.getState().show(rawBody);
      throw new ApiRequestError(402, rawBody.message);
    }

    // Someone else saved this item while you were editing it. Narrow strictly:
    // a 409 that is NOT an edit conflict (a duplicate-key clash, say) must fall
    // through to the generic path rather than be mis-rendered as a co-editor.
    if (res.status === 409 && isEditConflictPayload(detailValue)) {
      useEditConflictStore.getState().show(detailValue);
      throw new ApiRequestError(409, detailValue.message);
    }

    let detail: string;
    if (typeof detailValue === "string") {
      detail = detailValue;
    } else if (detailValue !== undefined) {
      detail =
        formatValidationDetail(detailValue) ??
        messageFromDetail(detailValue) ??
        JSON.stringify(detailValue);
    } else if (rawBody && typeof rawBody === "object") {
      detail = messageFromDetail(rawBody) ?? JSON.stringify(rawBody);
    } else {
      detail = res.statusText || `Request failed with status ${res.status}`;
    }
    const errors = Array.isArray(detailValue)
      ? (detailValue as ApiError["errors"])
      : undefined;

    // Surface other errors to Felix so he can help the user understand them.
    // Skip 401 (auth) — handled by the refresh flow above.
    if (res.status >= 400 && res.status !== 401 && !opts.background) {
      try {
        const { reportErrorToFelix } = await import("@/components/FelixChat");
        reportErrorToFelix(
          `${opts.method} ${opts.path} [${res.status}]: ${detail}`,
          res.status >= 500 ? "server" : "api",
        );
      } catch {
        // FelixChat not loaded yet — ignore
      }
    }
    // Row f14fbbc8: an app-side failure opens Felix with a ticket to file; a
    // user's own mistake opens his guidance. Only for a signed-in session: a
    // pre-auth call (login, register) has no Felix to open.
    if (!opts.skipAuth && !opts.background && getAccessToken()) {
      try {
        useAppErrorNoticeStore.getState().report({
          method: opts.method,
          path: opts.path,
          status: res.status,
          detail,
        });
      } catch {
        // The notice must never change the error the caller receives.
      }
    }
    throw new ApiRequestError(res.status, detail, errors, detailValue);
  }

  // Parse JSON response
  const data: T = await res.json();
  return data;
}

// ---------------------------------------------------------------------------
// Public HTTP method helpers
// ---------------------------------------------------------------------------

export const apiClient = {
  get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    return executeRequest<T>({ method: "GET", path, params, signal: options?.signal });
  },

  /**
   * @param options.skipAuth  Treat a 401 from THIS call as the answer, not as an
   *   expired session.
   *
   *   The pre-auth endpoints (login, register, password reset) need this. Without it a
   *   401 from `/auth/login` — i.e. "wrong password" — falls into the expired-session
   *   handler below, which attempts a token refresh, fails, and then CLEARS THE STORED
   *   CREDENTIALS AND REDIRECTS. To the user, a mistyped password looks like the app
   *   silently ate their session: a message flashes past and everything is gone.
   *   (Reported 2026-07-14 by an operator whose password had just been rotated.)
   */
  post<T>(path: string, body?: unknown, options?: { skipAuth?: boolean; background?: boolean }): Promise<T> {
    return executeRequest<T>({
      method: "POST", path, body, skipAuth: options?.skipAuth, background: options?.background,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return executeRequest<T>({ method: "PUT", path, body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return executeRequest<T>({ method: "PATCH", path, body });
  },

  delete<T = void>(path: string, body?: unknown): Promise<T> {
    // Some DELETEs carry a confirmation envelope (e.g. account deletion needs a
    // typed-email confirm gate). executeRequest already serializes `body` for
    // the other verbs; existing no-body callers are unaffected.
    return executeRequest<T>({ method: "DELETE", path, body });
  },

  /** Download a file as a Blob. Uses the same auth/retry logic as other methods. */
  async blob(path: string): Promise<{ blob: Blob; filename: string }> {
    const url = `${BASE_URL}${path}`;
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await fetch(url, { method: "GET", headers });
    } catch {
      throw new ApiRequestError(0, "Network error — check your connection and try again.");
    }

    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const retryHeaders: Record<string, string> = {};
        const newToken = getAccessToken();
        if (newToken) retryHeaders["Authorization"] = `Bearer ${newToken}`;
        try {
          const retryRes = await fetch(url, { method: "GET", headers: retryHeaders });
          if (!retryRes.ok) {
            const err = await parseErrorBody(retryRes);
            throw new ApiRequestError(err.status, err.detail, err.errors);
          }
          const disposition = retryRes.headers.get("Content-Disposition") || "";
          const match = disposition.match(/filename="?([^";\n]+)"?/);
          return { blob: await retryRes.blob(), filename: match?.[1] || "download" };
        } catch (retryErr) {
          if (retryErr instanceof ApiRequestError) throw retryErr;
          throw new ApiRequestError(0, "Network error during download retry.");
        }
      }
      clearTokens();
      window.location.href = "/login";
      throw new ApiRequestError(401, "Session expired. Redirecting to login.");
    }

    // Handle 204 No Content
    if (res.status === 204) {
      return { blob: new Blob(), filename: "download" };
    }

    if (!res.ok) {
      const err = await parseErrorBody(res);
      throw new ApiRequestError(err.status, err.detail, err.errors);
    }

    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    return { blob: await res.blob(), filename: match?.[1] || "download" };
  },

  /** Upload a FormData payload (multipart/form-data). Content-Type is set by the browser. */
  async upload<T>(path: string, formData: FormData): Promise<T> {
    const url = `${BASE_URL}${path}`;
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    // Do NOT set Content-Type — the browser sets it with the correct boundary
    let res: Response;
    try {
      res = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });
    } catch (networkErr) {
      throw new ApiRequestError(0, "Network error — check your connection and try again.");
    }
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const retryHeaders: Record<string, string> = {};
        const newToken = getAccessToken();
        if (newToken) retryHeaders["Authorization"] = `Bearer ${newToken}`;
        try {
          const retryRes = await fetch(url, { method: "POST", headers: retryHeaders, body: formData });
          if (!retryRes.ok) {
            const err = await parseErrorBody(retryRes);
            throw new ApiRequestError(err.status, err.detail, err.errors);
          }
          return retryRes.json();
        } catch (retryErr) {
          if (retryErr instanceof ApiRequestError) throw retryErr;
          throw new ApiRequestError(0, "Network error during upload retry.");
        }
      }
      clearTokens();
      window.location.href = "/login";
      throw new ApiRequestError(401, "Session expired. Redirecting to login.");
    }
    if (!res.ok) {
      const err = await parseErrorBody(res);
      throw new ApiRequestError(err.status, err.detail, err.errors);
    }
    return res.json();
  },
};

export default apiClient;
